import { useCallback, useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CandidateFilterType,
  CandidateFilterStateType,
  CandidateFilterPropertyKeys,
} from '@/components/CandidateFilter/CandidateFilter.types';
import useApiFetch from '@/hooks/useApiFetch';
import {
  fieldsAndFriendlyLabelsForFiltersList,
  fieldsAndFriendlyLabelsForFilters,
} from '@/pages/CandidateInfo/candidateInfoFields';
import candidatesServices from '@/services/candidates';
import usersApi from '@/services/user';
import { TechStackType, FetchApiIds, LocalStorageItemKeys } from '@/types';
import { ResponseApiAllUsers } from '@/types/api';
import * as Styles from './CandidateFilterManager.styles';
import CandidateFilter from '../CandidateFilter';

const defaultFilter = {
  field: '',
  condition: '',
  value: '',
  isOpened: true,
  showingDeleteButton: false,
  position: CandidateFilterPropertyKeys.FIELD,
  prevCondition: '',
  prevValue: '',
};

export type CandidateFiltersManagerProps = {
  onSelectFilters: (filters: any) => void;
  preDefinedFilters: CandidateFilterType[];
  isAddingFiltersHandler: (isAdding: boolean) => void;
};

const CandidateFiltersManager = ({
  isAddingFiltersHandler,
  onSelectFilters,
  preDefinedFilters,
}: CandidateFiltersManagerProps): JSX.Element => {
  const formatFilters = (filtersMap: CandidateFilterType[]): CandidateFilterStateType[] => {
    const formattedFilters = filtersMap
      .map((filter) => {
        const [, field = ''] =
          fieldsAndFriendlyLabelsForFiltersList.find(([key]) => key === filter.field) || [];

        return {
          ...filter,
          field,
          isOpened: false,
          showingDeleteButton: false,
          position: CandidateFilterPropertyKeys.COMPLETE,
        };
      })
      .filter((filter) => !!filter.field);

    return formattedFilters;
  };

  const [filters, setFilters] = useState<CandidateFilterStateType[]>(
    formatFilters(preDefinedFilters || [])
  );

  const allTechStacksResponse = useApiFetch<TechStackType[], void>(
    FetchApiIds.TECH_STACKS,
    candidatesServices.getAllTechsStacks
  );

  const allUsersResponse = useApiFetch<ResponseApiAllUsers>(FetchApiIds.GET_ALL_UERS, async () =>
    usersApi.getAllUsers({
      page: 1,
      take: 999,
    })
  );

  const cleanAllFilters = (): void => {
    setFilters([]);
  };

  useEffect(() => {
    isAddingFiltersHandler(!!filters.length);
  }, [filters, isAddingFiltersHandler]);

  useEffect(() => {
    if (!preDefinedFilters?.length) {
      return;
    }

    const formattedFilters = formatFilters(preDefinedFilters);

    setFilters(formattedFilters);
  }, [preDefinedFilters]);

  const appliedFilters = useMemo(() => {
    return filters
      .filter((filter) => {
        const { condition, field, value } = filter;
        return condition && field && (value || value === null);
      })
      .filter((filter) => {
        return Array.isArray(filter.value) ? filter.value.length : true;
      })
      .filter((filter) => {
        return filter.value !== 'specific-day' && filter.value !== 'custom-range';
      })
      .map((filter) => {
        const { condition, field, value } = filter;

        const propertyKey = fieldsAndFriendlyLabelsForFiltersList.find(
          ([, label]) => label === field
        )?.[0];

        return {
          field: propertyKey,
          condition,
          value,
        };
      });
  }, [filters]);

  const formattedAppliedFilters = useMemo(() => {
    if (!appliedFilters.length) {
      return '';
    }

    const filtersAsString = JSON.stringify(
      appliedFilters.map((filter) => ({
        ...filter,
        value: filter.value === null ? '' : filter.value,
      }))
    );

    return encodeURIComponent(filtersAsString);
  }, [appliedFilters]);

  const saveFiltersOnUrl = (filtersToApply): void => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const savedFiltersOnURL = urlParams.get('filters');

    const newFilters = JSON.stringify(filtersToApply);
    const normalizedSavedFilters = savedFiltersOnURL ?? '[]';

    if (newFilters !== normalizedSavedFilters) {
      if (filtersToApply.length) {
        urlParams.set('filters', newFilters);
      } else {
        urlParams.delete('filters');
      }

      const updatedQueryString = urlParams.toString();
      const updatedPath = `${window.location.pathname}${
        updatedQueryString ? `?${updatedQueryString}` : ''
      }`;

      window.history.pushState(null, '', updatedPath);
    }
  };

  const saveFiltersOnLocalStorage = (filtersToApply): void => {
    window.localStorage.setItem(LocalStorageItemKeys.FILTERS, JSON.stringify(filtersToApply));
  };

  useEffect(() => {
    saveFiltersOnUrl(appliedFilters);
    saveFiltersOnLocalStorage(appliedFilters);
    onSelectFilters(formattedAppliedFilters);
  }, [formattedAppliedFilters, appliedFilters, onSelectFilters]);

  const onAddNewFilter = (): void => {
    setFilters((prevFilters) => {
      return prevFilters
        .map((filter) => ({
          ...filter,
          showingDeleteButton: false,
          isOpened: false,
        }))
        .concat(defaultFilter);
    });
  };

  const getNextProperty = useCallback((filter) => {
    let nextPosition = CandidateFilterPropertyKeys.CONDITION;

    if (filter?.position === CandidateFilterPropertyKeys.CONDITION) {
      nextPosition = CandidateFilterPropertyKeys.VALUE;
    }

    if (filter?.position === CandidateFilterPropertyKeys.VALUE) {
      nextPosition = CandidateFilterPropertyKeys.COMPLETE;
    }

    if (filter?.position === CandidateFilterPropertyKeys.COMPLETE) {
      nextPosition = CandidateFilterPropertyKeys.COMPLETE;
    }

    return nextPosition;
  }, []);

  const completeFilterAsEmpty = useCallback((filter, value) => {
    const currentPosition = filter.position;
    return {
      ...filter,
      prevValue: ['value', 'condition'].includes(currentPosition) ? '' : filter.prevValue,
      prevCondition: currentPosition === 'condition' ? '' : filter.prevCondition,
      [currentPosition]: value,
      value: null,
      isOpened: false,
      position: CandidateFilterPropertyKeys.COMPLETE,
    };
  }, []);

  const updateMultipleValues = useCallback(
    (filter, value) => {
      const currentPosition = filter.position;
      const nextPosition = getNextProperty(filter);

      return {
        ...filter,
        isOpened: true,
        prevValue: ['value', 'condition'].includes(currentPosition) ? '' : filter.prevValue,
        prevCondition: currentPosition === 'condition' ? '' : filter.prevCondition,
        [currentPosition]: value,
        position: nextPosition,
        value: Array.isArray(filter.value) ? filter.value.concat(value) : [],
      };
    },
    [getNextProperty]
  );

  const updateDateRange = useCallback((filter, value) => {
    const currentPosition = filter.position;
    return {
      ...filter,
      isOpened: false,
      [currentPosition]: value,
      position: CandidateFilterPropertyKeys.COMPLETE,
    };
  }, []);

  const updateDateSelection = useCallback((filter, value) => {
    const currentPosition = filter.position;
    return {
      ...filter,
      isOpened: true,
      [currentPosition]: value,
      position: CandidateFilterPropertyKeys.VALUE,
    };
  }, []);

  const onUpdateFilters = useCallback(
    (filter: CandidateFilterStateType, filterIndex, value, index) => {
      if (filterIndex !== index) {
        return filter;
      }

      const nextPosition = getNextProperty(filter);
      const currentPosition = filter.position;
      const isPositionCondition = currentPosition === CandidateFilterPropertyKeys.CONDITION;
      const valueContainsEmpty = value.toString().includes('Empty');
      const isMultiple = [fieldsAndFriendlyLabelsForFilters.skills].includes(filter.field);
      const isDateSelection = ['specific-day', 'custom-range'].includes(value);
      const isDateRange = filter.condition === 'isWithin';

      if (isDateSelection) {
        return updateDateSelection(filter, value);
      }

      if (isDateRange) {
        return updateDateRange(filter, value);
      }

      if (isPositionCondition && valueContainsEmpty) {
        return completeFilterAsEmpty(filter, value);
      }

      if (isMultiple) {
        return updateMultipleValues(filter, value);
      }

      return {
        ...filter,
        prevValue: ['value', 'condition'].includes(currentPosition) ? '' : filter.prevValue,
        prevCondition: currentPosition === 'condition' ? '' : filter.prevCondition,
        isOpened: nextPosition !== CandidateFilterPropertyKeys.COMPLETE,
        [currentPosition]: value,
        position: nextPosition,
      };
    },
    [
      getNextProperty,
      updateDateSelection,
      updateDateRange,
      completeFilterAsEmpty,
      updateMultipleValues,
    ]
  );

  const onSelectFilterOption = (value, index): void => {
    setFilters((prevFilters) => {
      return prevFilters.map((filter, filterIndex) => {
        return onUpdateFilters(filter, filterIndex, value, index);
      });
    });
  };

  const onRemoveFilterOption = (value, index): void => {
    setFilters((prevFilters) => {
      return prevFilters.map((filter, filterIndex) => {
        if (index !== filterIndex) {
          return filter;
        }

        return {
          ...filter,
          value: Array.isArray(filter.value)
            ? filter.value.filter((v) => v !== value)
            : filter.value,
        };
      });
    });
  };

  const onRemoveFilterHandler = useCallback((index) => {
    setFilters((prevFilters) => {
      return prevFilters.filter((filter, filterIndex) => {
        return filterIndex !== index;
      });
    });
  }, []);

  const showButtonToDelete = (index: number): void => {
    setFilters((prevFilters) => {
      return prevFilters.map((filter, filterIndex) => ({
        ...filter,
        showingDeleteButton: filterIndex === index,
      }));
    });
  };

  const containsEmptyFields = useMemo(() => {
    return filters.some((filter) => !filter.field);
  }, [filters]);

  const watchPageClick = useCallback(
    (event) => {
      const eventPath = event.composedPath() ?? [];
      const clickedIndex = filters.findIndex((filter, index) => {
        return eventPath.some(({ id }) => {
          return id?.includes(`candidate-filter-${index}`);
        });
      });

      setFilters((prevFilters) => {
        return prevFilters
          .map((filter, index) => {
            const clickedItem = index === clickedIndex;

            return {
              ...filter,
              showingDeleteButton: false,
              condition: filter.prevCondition || filter.condition,
              value: filter.prevValue || filter.value,
              isOpened: clickedItem && filter.position !== CandidateFilterPropertyKeys.COMPLETE,
            };
          })
          .filter((filter) => !!filter.field);
      });
    },
    [filters]
  );

  useEffect(() => {
    window.addEventListener('click', watchPageClick);
    return () => window.removeEventListener('click', watchPageClick);
  }, [watchPageClick]);

  const getCurrentPosition = useCallback((filter) => {
    if (filter.condition) {
      return CandidateFilterPropertyKeys.VALUE;
    }
    return CandidateFilterPropertyKeys.CONDITION;
  }, []);

  const updateEmptyCondition = useCallback((filter) => {
    return {
      ...filter,
      prevCondition: filter.condition,
      value: '',
      condition: '',
      showingDeleteButton: false,
      position: CandidateFilterPropertyKeys.CONDITION,
      isOpened: true,
    };
  }, []);

  const updateClickedFilterValue = useCallback(
    (filter) => {
      const position = getCurrentPosition(filter);
      return {
        ...filter,
        showingDeleteButton: false,
        prevValue: filter.value,
        value: '',
        position,
        isOpened: true,
      };
    },
    [getCurrentPosition]
  );

  const updateClickedFilterCondition = useCallback((filter) => {
    return {
      ...filter,
      prevCondition: filter.condition,
      prevValue: filter.prevValue || filter.value,
      condition: '',
      value: '',
      isOpened: true,
      position: CandidateFilterPropertyKeys.CONDITION,
      showingDeleteButton: false,
    };
  }, []);

  const onClickValue = (clickedIndex: number): void => {
    setFilters((prevFilters) => {
      return prevFilters
        .map((filter, index) => {
          if (clickedIndex !== index) {
            return {
              ...filter,
              showingDeleteButton: false,
              isOpened: false,
            };
          }

          if (filter.condition.includes('Empty')) {
            return updateEmptyCondition(filter);
          }

          return updateClickedFilterValue(filter);
        })
        .filter((filter) => !!filter.field);
    });
  };

  const onClickCondition = (clickedIndex: number): void => {
    setFilters((prevFilters) => {
      return prevFilters
        .map((filter, index) => {
          if (clickedIndex !== index) {
            return {
              ...filter,
              isOpened: false,
            };
          }

          return updateClickedFilterCondition(filter);
        })
        .filter((filter) => !!filter.field);
    });
  };

  return (
    <Styles.Box data-testid="candidate-filters-manager">
      <Styles.Text>Where</Styles.Text>
      <Styles.FiltersArea>
        {filters.map((filter, index) => (
          <CandidateFilter
            technologiesOptions={allTechStacksResponse.data ?? []}
            allUsers={allUsersResponse.data?.users.data ?? []}
            onClickCondition={() => onClickCondition(index)}
            onClickValue={() => onClickValue(index)}
            id={`candidate-filter-${index}`}
            showButtonToDelete={() => showButtonToDelete(index)}
            onRemoveFilterHandler={() => onRemoveFilterHandler(index)}
            filter={filter}
            onRemoveFilterOption={(value) => onRemoveFilterOption(value, index)}
            onSelectFilterOption={(value) => onSelectFilterOption(value, index)}
            key={`filter-${index.toString()}`}
            data-testid={`candidate-filter-${index}`}
          />
        ))}
        <Styles.AddFilters
          withMarginTop={!!filters.length}
          data-testid="add-new-filter-btn"
          disabled={containsEmptyFields}
          onClick={(event) => {
            event.stopPropagation();
            onAddNewFilter();
          }}
        >
          + Filter
        </Styles.AddFilters>
        {!!filters.length && (
          <Styles.CustomTransparentButton
            withMarginTop={!!filters.length}
            onClick={cleanAllFilters}
            isRedButton
          >
            <>
              <DeleteIcon fontSize="small" />
              Remove all filters
            </>
          </Styles.CustomTransparentButton>
        )}
      </Styles.FiltersArea>
    </Styles.Box>
  );
};

export default CandidateFiltersManager;
